import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useToastStore } from "@/components/PersistentToast/store/toast";
import { useIsSessionActive } from "@/hooks/useIsSessionActive";
import { logoutUser } from "@/utils/tokenManager";

import { FileItem } from "./FileItem";

export type RehydratedTask = {
  taskId: string;
  fileName: string;
  fileSize?: number;
  phase: "polling" | "result";
};

type FileListProps = {
  files?: File[];
  // STP-77: server-recovered tasks rendered alongside fresh uploads
  rehydratedTasks?: RehydratedTask[];
  userId: string;
  caseNumber: string;
  onToastClose?: () => void;
  onAllCleared?: () => void;
};

type Row =
  | { kind: "fresh"; key: string; file: File }
  | {
      kind: "rehydrated";
      key: string;
      file: File;
      initialState: { taskId: string; phase: "polling" | "result" };
    };

export const FileList = ({
  files,
  rehydratedTasks,
  userId,
  caseNumber,
  onToastClose,
  onAllCleared,
}: FileListProps) => {
  const [completedFiles, setCompletedFiles] = useState<Set<string>>(new Set());
  const [dismissedFiles, setDismissedFiles] = useState<Set<string>>(new Set());
  const [anyNeedsReview, setAnyNeedsReview] = useState(false);
  const hasAutoMinimized = useRef(false);
  const { toggleExpand, isExpanded, closeToast } = useToastStore();

  // MDRS-267: this toast is mounted outside the router/ProtectedRoute tree, so
  // nothing tears it down when the session ends — it would sit there showing
  // stale "live" progress. Watch session validity and, on a genuine
  // active→inactive transition, close the modal, clear the dead session, and
  // redirect to re-authenticate.
  const isSessionActive = useIsSessionActive();
  const wasSessionActiveRef = useRef(false);
  const hasHandledExpiryRef = useRef(false);

  useEffect(() => {
    if (isSessionActive) {
      wasSessionActiveRef.current = true;
      return;
    }
    // Ignore an initial/bootstrap render that momentarily reports inactive —
    // only react once we've observed a live session go away.
    if (!wasSessionActiveRef.current || hasHandledExpiryRef.current) return;
    hasHandledExpiryRef.current = true;

    closeToast();
    // Fire-and-forget is safe here: with redirect=false, logoutUser clears the
    // token + storage synchronously (it only awaits on the redirect=true MSAL
    // path), so the state is gone before the hard redirect below. Keep this
    // ordering if logoutUser is refactored.
    logoutUser(false, "Bundle toast: session expired");
    // Hard redirect (not router navigation): the toast lives outside the router.
    window.location.href = "/";
  }, [isSessionActive, closeToast]);

  // Rehydrated tasks come from the backend list endpoint and don't have File
  // blobs. FilePreview only reads name + size, so we build a zero-byte
  // placeholder and override `size` to the persisted value via defineProperty
  // (avoids allocating real bytes for large files).
  const rows = useMemo<Row[]>(() => {
    const fresh: Row[] = (files ?? []).map((file) => ({
      kind: "fresh",
      key: file.name,
      file,
    }));
    const rehydrated: Row[] = (rehydratedTasks ?? []).map((t) => {
      const placeholder = new File([], t.fileName);
      if (t.fileSize != null && t.fileSize > 0) {
        Object.defineProperty(placeholder, "size", {
          value: t.fileSize,
          configurable: true,
        });
      }
      return {
        kind: "rehydrated",
        key: t.taskId,
        file: placeholder,
        initialState: { taskId: t.taskId, phase: t.phase },
      };
    });
    return [...fresh, ...rehydrated];
  }, [files, rehydratedTasks]);

  const visibleRows = rows.filter((r) => !dismissedFiles.has(r.key));

  const handleComplete = useCallback((rowKey: string, needsReview: boolean) => {
    setCompletedFiles((prev) => {
      const next = new Set(prev);
      next.add(rowKey);
      return next;
    });
    if (needsReview) {
      setAnyNeedsReview(true);
    }
  }, []);

  const handleDismiss = useCallback((rowKey: string) => {
    setDismissedFiles((prev) => {
      const next = new Set(prev);
      next.add(rowKey);
      return next;
    });
  }, []);

  // Auto-minimize toast once when all rows complete
  useEffect(() => {
    if (
      rows.length > 0 &&
      completedFiles.size === rows.length &&
      !anyNeedsReview &&
      isExpanded &&
      !hasAutoMinimized.current
    ) {
      hasAutoMinimized.current = true;
      const timer = setTimeout(() => {
        toggleExpand();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [completedFiles.size, rows.length, isExpanded, toggleExpand]);

  if (visibleRows.length === 0) {
    if (onAllCleared) {
      onAllCleared();
    } else {
      onToastClose?.();
    }
    return null;
  }

  return (
    <div className="-mb-4 max-h-[50vh] min-w-[32rem] overflow-auto">
      <div className="space-y-3 pb-4 pr-2">
        {visibleRows.map((row) => (
          <FileItem
            key={row.key}
            file={row.file}
            userId={userId}
            caseNumber={caseNumber}
            initialState={row.kind === "rehydrated" ? row.initialState : undefined}
            onToastClose={onToastClose}
            onComplete={(needsReview) => handleComplete(row.key, needsReview)}
            onDismiss={() => handleDismiss(row.key)}
          />
        ))}
      </div>
    </div>
  );
};
